import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import Loader from '../../../components/common/Loader'
import ValidationBanner from '../../../components/common/ValidationBanner'
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import { getApproverRequestByTorId } from '../../../store/slices/Approver/approverRequestDetailsSlice'
import { RootState } from '../../../store/store'
import RequestInfo from './RequestInfo'
import RequestDetailsView from './RequestDetailsView'
import { usePrint } from '../../../hooks/usePrint'

/**
 * RequestProcessContainer component — fetches and displays the detailed status of a time off request
 * from the approver perspective with balance, permissions, and upcoming hours info.
 * Uses the approverRequestDetails Redux slice to manage request state.
 */

const getRequestTitle = (status?: string): string => {
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
    case 'APPROVED_PRTSUBMITTED':
      return 'Approved Time Off Request'

    case 'OVERRIDDEN_APPROVED':
    case 'OVERRIDDEN':
      return 'Approved Time Off Request (Override)'
    case 'PRTDENIED':
      return 'Time Off Request Partial Retraction Denied'
    case 'PAPPROVED':
      return 'Partially Approved Time Off Request'

    case 'DENIED':
      return 'Denied Time Off Request'

    case 'PDENIED':
      return 'Partially Denied Time Off Request'

    case 'RETURNED':
      return 'Returned Time Off Request'

    case 'PRTSUBMITTED':
    case 'PRTSUBMITTED_RELEASED':
      return 'Process a Time Off Request Partial Retraction'

    case 'RTSUBMITTED':
    case 'RTSUBMITTED_RELEASED':
      return 'Process a Time Off Request Retraction'

    case 'PRETRACTED':
      return 'Time Off Request Partial Retraction Approved'
    case 'PRTDENIED_OVERRIDDEN':
      return 'Approved Partial Retraction Request (Override)'
    case 'RETRACTED':
      return 'Time Off Request Retraction Approved'
    case 'RTAPPROVED':
      return 'Time Off Request Retraction Approved'
    case 'RTDENIED_OVERRIDDEN':
      return 'Time Off Request Retraction Approved'

    case 'RTDENIED':
    case 'OVERRIDDEN_RTDENIED':
      return 'Time Off Request Retraction Denied'

    case 'TORPAPPROVEDRETRACTIONDENIED':
      return 'Time Off Request Partial Retraction Denied'

    case 'PRTSUBMITTED_ONHOLD':
      return 'Request for Time Off Partial Retraction On Hold'

    case 'WITHDRAWN':
      return ''

    case 'SUBMITTED':
    case 'SUBMITTED_RELEASED':
    case 'RESUBMITTED':
    case 'RESUBMITTED_RELEASED':
    case 'SUBMITTED_ONHOLD':
    case 'RESUBMITTED_ONHOLD':
    case 'ONHOLD':
    default:
      return 'Process a Time Off Request'
  }
}

const RequestProcessContainer: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { data, loading, error } = useAppSelector(
    (state: RootState) => state.approverRequestDetails
  )

  // Approved hours mapping shared between the action form (left) and the
  // Current Request table (right). Keys are stringified toReqDtlId values.
  const [approvedHoursById, setApprovedHoursById] = useState<Record<string, number>>({})

  // Scopes the Print button to the request details/summary/audit history/
  // calendar below, matching legacy's print-friendly view — the header,
  // navigation, and breadcrumbs above are never included.
  const { printRef, handlePrint } = usePrint()

  const title = getRequestTitle(data?.statuses?.[0]?.status)
  useEffect(() => {
    if (!requestId) return
    dispatch(getApproverRequestByTorId(requestId))
  }, [requestId, dispatch])

  const handleBreadcrumbClick = () => {
    navigate('/approver')
  }

  if (loading) {
    return <Loader />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="grid-container w-full">
          <div className="w-100%">
            <ValidationBanner variant="error" heading="Error" message={error} compact={false} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container min-h-[70vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <BreadCrumbs
        previousPage="Manage Time Off Requests"
        currentPage="Process Request Detail"
        onPreviousClick={handleBreadcrumbClick}
      />

      <div className="padding-bottom-2u margin-top-4u desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-3.5!">
        <h1 className="h1">{title}</h1>
      </div>

      {/* Request Details View - Status forms and views */}
      <div className="space-y-8" ref={printRef}>
        {data && (
          <RequestDetailsView
            data={data}
            onPrint={handlePrint}
            onApprovedChange={(payload) => {
              // Convert byRow array into a simple id -> approvedHours map
              const map: Record<string, number> = {}
              payload.byRow.forEach((r: any) => {
                map[String(r.id)] = r.approvedHours
              })
              setApprovedHoursById(map)
            }}
            // Whenever the active form changes, clear the mapping so the
            // Current Request table always reflects the default requested
            // hours for new statuses and the partial form starts fresh.
            onActiveFormChange={() => setApprovedHoursById({})}
          />
        )}
        {data && <RequestInfo data={data} approvedHoursById={approvedHoursById} />}
      </div>
    </div>
  )
}

export default RequestProcessContainer
